import Vue from "vue";
import VueRouter, { Route } from "vue-router";
import QuickQuestionsView from "@/views/quick-questionsView.vue";
import ComparisonView from "@/views/comparisonView.vue";
import loginView from "@/views/loginView.vue";
import SettingsView from "@/views/settingsView.vue";
import ForgotPasswordView from "@/views/ForgotPasswordView.vue";
import ResetPasswordView from "@/views/ResetPasswordView.vue";
import user, { userHasRole } from "@/store/modules/user";
import AdministrationView from "@/views/accountAdministrationView.vue";
import ProcessFormView from "@/views/formView.vue";
import ProcessFormTokenView from "@/views/formTokenView.vue";
import ManageOrganizationView from "@/views/manageOrganizationView.vue";
import OrchestratorReleasesView from "@/views/orchestratorReleasesView.vue";
import ProcessFormBuilderView from "@/views/formBuilderView.vue";

export interface WithRoute {
  $route: Route;
  $router: VueRouter;
}

Vue.use(VueRouter);

export enum ROUTE_NAMES {
  ADMINISTRATION = "administration",
  ASSESSMENTS = "assessments",
  FORGOT_PASSWORD = "forgot-password",
  FORM = "form",
  FORM_BUILDER = "formbuilder",
  LOGIN = "login",
  ORCHESTRATOR_PROCESS_TOKEN = "orchestrator_token",
  ORCHESTRATOR_RELEASES = "orchestrator_releases",
  ORGANIZATION = "organization",
  QUESTIONS = "questions",
  RESET_PASSWORD = "reset-password",
  SETTINGS = "settings"
}

const routes = [
  {
    path: "/login",
    name: ROUTE_NAMES.LOGIN,
    component: loginView
  },
  {
    path: "*",
    redirect: "/assessments"
  },
  {
    path: "/questions",
    name: ROUTE_NAMES.QUESTIONS,
    component: QuickQuestionsView,
    props: true
  },
  {
    path: "/assessments/:userName?",
    name: ROUTE_NAMES.ASSESSMENTS,
    component: ComparisonView,
    props: true
  },
  {
    path: "/settings",
    name: ROUTE_NAMES.SETTINGS,
    component: SettingsView
  },
  {
    path: "/administration",
    name: ROUTE_NAMES.ADMINISTRATION,
    component: AdministrationView,
    beforeEnter: isAdmin
  },
  {
    path: "/forgot-password",
    name: ROUTE_NAMES.FORGOT_PASSWORD,
    component: ForgotPasswordView
  },
  {
    path: "/reset-password/:token",
    name: ROUTE_NAMES.RESET_PASSWORD,
    component: ResetPasswordView
  },
  {
    path: "/organization",
    name: ROUTE_NAMES.ORGANIZATION,
    component: ManageOrganizationView,
    props: true,
    beforeEnter: isOrganizationManager
  },
  {
    path: "/processes",
    name: ROUTE_NAMES.ORCHESTRATOR_RELEASES,
    component: OrchestratorReleasesView,
    props: true,
    beforeEnter: isOrganizationManager
  },
  {
    path: "/release/:releaseId/formbuilder",
    name: ROUTE_NAMES.FORM_BUILDER,
    component: ProcessFormBuilderView,
    props: true,
    beforeEnter: isOrganizationManager
  },
  {
    path: "/release/:releaseId/form",
    name: ROUTE_NAMES.FORM,
    component: ProcessFormView,
    props: true,
    beforeEnter: isOrganizationManager
  },
  {
    path: "/form/:token",
    name: ROUTE_NAMES.ORCHESTRATOR_PROCESS_TOKEN,
    component: ProcessFormTokenView,
    props: true
  }
];
const ADMIN = "ADMIN";
const ORG_MANAGER = "ORG_MANAGER";
const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes
});
router.beforeEach(async (to, from, next) => {
  // if the route doesn't require being logged in
  if (
    to.name === ROUTE_NAMES.FORGOT_PASSWORD ||
    to.name === ROUTE_NAMES.RESET_PASSWORD ||
    to.name === ROUTE_NAMES.ORCHESTRATOR_PROCESS_TOKEN
  ) {
    next();
    return;
  }

  // check if the user is logged in
  if (await user.testLogin()) {
    // if they are trying to access the login page...
    if (to.name === ROUTE_NAMES.LOGIN) {
      //...redirect
      next({ name: ROUTE_NAMES.ASSESSMENTS });
      return;
    } else {
      // otherwise, proceed
      next();
      return;
    }
  } else {
    // if the user is not logged in, and they try to go to the login page
    if (to.name === ROUTE_NAMES.LOGIN) {
      next();
      return;
    } else {
      next({name: ROUTE_NAMES.LOGIN});
      return;
    }
  }
});

async function isAdmin(to: Route, from: Route, next: Function) {
  if (userHasRole(ADMIN)) {
    // if the user is admin, proceed
    next();
  } else {
    // this user is not allowed, redirect to a safe page
    next({ name: ROUTE_NAMES.ASSESSMENTS });
  }
}
async function isOrganizationManager(to: Route, from: Route, next: Function) {
  if (userHasRole(ORG_MANAGER) || userHasRole(ADMIN)) {
    // if the user is organization manager, proceed
    next();
  } else {
    // this user is not allowed, redirect to a safe page
    next({ name: ROUTE_NAMES.ASSESSMENTS });
  }
}

export default router;

export function getRouteByName(routeName: ROUTE_NAMES) {
  return routes.find(r => r.name === routeName);
}
